import { ReactNode } from "react"
import { create } from "zustand"

type SlideoverSize = "half" | "sm" | "md" | "lg" | "xl" | "2xl" | "base"

interface OpenSlideoverProps {
  content: ReactNode
  closeCallback?: () => void
  headerTitle?: ReactNode
  size?: SlideoverSize
  rightHeaderContent?: ReactNode
}

interface SlideoverHistoryEntry {
  content: ReactNode
  headerTitle?: ReactNode
  size: SlideoverSize
  rightHeaderContent?: ReactNode
  afterClose: () => void
}

interface GlobalSlideoverState {
  isOpen: boolean
  content: ReactNode | null
  headerTitle?: ReactNode
  size: SlideoverSize
  rightHeaderContent?: ReactNode
  afterClose: () => void
  history: SlideoverHistoryEntry[]
  canGoBack: boolean
  openSlideover: (props: OpenSlideoverProps) => void
  pushSlideover: (props: OpenSlideoverProps) => void
  popSlideover: () => void
  closeSlideover: () => void
  setHeaderTitle: (content: ReactNode) => void
  setRightHeaderContent: (content: ReactNode) => void
}

export const useGlobalSlideoverStore = create<GlobalSlideoverState>((set, get) => ({
  isOpen: false,
  content: null,
  headerTitle: undefined,
  size: "base",
  rightHeaderContent: null,
  afterClose: () => {},
  history: [],
  canGoBack: false,

  openSlideover: ({
    content,
    closeCallback = () => {},
    headerTitle,
    size = "base",
    rightHeaderContent,
  }: OpenSlideoverProps) =>
    set(() => ({
      isOpen: true,
      content,
      headerTitle,
      size,
      rightHeaderContent,
      afterClose: closeCallback,
      history: [],
      canGoBack: false,
    })),

  pushSlideover: ({
    content,
    closeCallback = () => {},
    headerTitle,
    size = "base",
    rightHeaderContent,
  }: OpenSlideoverProps) => {
    const state = get()
    const currentEntry: SlideoverHistoryEntry = {
      content: state.content,
      headerTitle: state.headerTitle,
      size: state.size,
      rightHeaderContent: state.rightHeaderContent,
      afterClose: state.afterClose,
    }

    set(() => ({
      isOpen: true,
      content,
      headerTitle,
      size,
      rightHeaderContent,
      afterClose: closeCallback,
      history: [...state.history, currentEntry],
      canGoBack: true,
    }))
  },

  popSlideover: () => {
    const state = get()
    if (state.history.length === 0) {
      get().closeSlideover()
      return
    }

    const newHistory = [...state.history]
    const previousEntry = newHistory.pop()!

    set(() => ({
      content: previousEntry.content,
      headerTitle: previousEntry.headerTitle,
      size: previousEntry.size,
      rightHeaderContent: previousEntry.rightHeaderContent,
      afterClose: previousEntry.afterClose,
      history: newHistory,
      canGoBack: newHistory.length > 0,
    }))
  },

  closeSlideover: () => {
    const state = get()
    if (!state.isOpen) return

    state.afterClose()
    set({
      isOpen: false,
      content: null,
      headerTitle: undefined,
      rightHeaderContent: null,
      history: [],
      canGoBack: false,
      afterClose: () => {},
    })
  },

  setHeaderTitle: (content: ReactNode) => set({ headerTitle: content }),
  setRightHeaderContent: (content: ReactNode) => set({ rightHeaderContent: content }),
}))
